// public/js/ui/help.js
// One consistent in-app help-text system — see docs/help-text.md for the design,
// the writing guide and worked examples.
//
// Three delivery types, one look, one wiring call:
//   • ICON   — a "?" next to a label. Hover/focus reveals the text. Use for
//              settings and controls the user actively configures.
//   • HOVER  — attach to an EXISTING element (a status pill, a value, a button)
//              so hovering it reveals the text, with no extra icon. Use when a
//              read-only user needs to know what something MEANS.
//   • INLINE — a muted line of guidance rendered directly under a field. Use for
//              a hint (format, unit, gotcha) that should always be visible.
//
// Copy lives in a per-feature dictionary { key: htmlString } passed in by the
// caller, so the words sit next to the feature. Text may be rich HTML.
import { escapeHtml } from '../utils/escape.js';

// A "?" help icon. Reveals dict[key] once initHelp() has wired it.
export function helpIcon(key) {
  return `<i class="fa-regular fa-circle-question app-help-icon text-muted ms-1" `
       + `data-help="${escapeHtml(key)}" role="button" tabindex="0" aria-label="Help"></i>`;
}

// Attributes that make an EXISTING element reveal dict[key] on hover/focus.
// Spread into the element's tag, e.g.
//   `<span class="badge" ${helpHover('pill.docked')}>Docked</span>`
// Don't also set a native title= on the same element — it fights the tooltip.
export function helpHover(key) {
  return `data-help="${escapeHtml(key)}" tabindex="0"`;
}

// A muted inline help line, resolved now (no wiring needed). Returns '' when the
// key has no copy, so it can be dropped into a template unconditionally.
export function helpInline(key, dict) {
  const html = dict && dict[key];
  return html ? `<div class="app-help-inline text-muted small mt-1">${html}</div>` : '';
}

// Wire every [data-help] within `container` to a Bootstrap tooltip whose content
// is dict[key]. Idempotent (skips already-wired nodes) — safe to call on every
// re-render. Call AFTER the container's HTML is in the DOM, on the
// always-rendered path so read-only users get their hover tooltips too.
export function initHelp(container, dict) {
  if (!container || !dict) return;
  container.querySelectorAll('[data-help]').forEach(el => {
    if (el.dataset.helpInit) return;
    const html = dict[el.dataset.help];
    if (!html) return;                       // no copy for this key → leave as-is
    el.dataset.helpInit = '1';
    try {
      new bootstrap.Tooltip(el, {
        title: html, html: true, customClass: 'app-help',
        trigger: 'hover focus', placement: 'auto', boundary: 'viewport',
      });
    } catch { /* bootstrap missing — help is non-critical, never break render */ }
  });
}
